import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ClerkProvider, SignIn, SignUp } from '@clerk/clerk-react'

import AuthGuard from '@/components/layout/AuthGuard'
import AppLayout from '@/components/layout/AppLayout'

// Pages
import LandingPage from '@/pages/LandingPage'
import ApplicationsPage from '@/pages/ApplicationsPage'
import ProgramsPage from '@/pages/ProgramsPage'
import RecommendersPage from '@/pages/RecommendersPage'
import DocumentsPage from '@/pages/DocumentsPage'
import ProfilePage from '@/pages/ProfilePage'
import PostAdmitPage from '@/pages/PostAdmitPage'
import CounselorPage from '@/pages/CounselorPage'
import SettingsPage from '@/pages/SettingsPage'
import BillingPage from '@/pages/BillingPage'
import RecommenderPortalPage from '@/pages/RecommenderPortalPage'

/**
 * Clerk publishable key.
 *
 * Must be set as VITE_CLERK_PUBLISHABLE_KEY in the environment.
 * The app will throw at startup if the key is missing, which surfaces the
 * misconfiguration early rather than producing a confusing auth failure.
 */
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!clerkPublishableKey) {
  throw new Error(
    'Missing required environment variable: VITE_CLERK_PUBLISHABLE_KEY\n' +
      'Copy .env.example to .env.local and fill in the values.',
  )
}

// Placeholder keys from .env.example won't connect to Clerk and cause a silent white screen.
if (clerkPublishableKey === 'pk_test_...' || clerkPublishableKey.endsWith('...')) {
  throw new Error(
    'VITE_CLERK_PUBLISHABLE_KEY is still set to the placeholder value from .env.example.\n' +
      'Replace it with your real key from https://dashboard.clerk.com → API Keys.',
  )
}

/**
 * App — root component.
 *
 * Route structure:
 *
 *   /                                 — LandingPage (public marketing page with embedded sign-in)
 *   /sign-in/*                        — Clerk hosted sign-in (path routing, used for OAuth callbacks)
 *   /sign-up/*                        — Clerk hosted sign-up (path routing, used for email verification)
 *   /public/recommendation/:token     — Public recommender portal (no auth)
 *   /applications                     — AuthGuard → AppLayout → ApplicationsPage
 *   /programs                         — AuthGuard → AppLayout → ProgramsPage
 *   /recommenders                     — AuthGuard → AppLayout → RecommendersPage
 *   /documents                        — AuthGuard → AppLayout → DocumentsPage
 *   /profile                          — AuthGuard → AppLayout → ProfilePage
 *   /post-admit                       — AuthGuard → AppLayout → PostAdmitPage
 *   /settings                         — AuthGuard → AppLayout → SettingsPage
 *   /billing                          — AuthGuard → AppLayout → BillingPage
 */
export default function App() {
  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      signInFallbackRedirectUrl="/applications"
      signUpFallbackRedirectUrl="/applications"
    >
      <Router>
        <Routes>
          {/* Public marketing landing page — no auth required */}
          <Route path="/" element={<LandingPage />} />

          {/* Clerk auth routes — kept for OAuth callbacks and email verification flows */}
          <Route
            path="/sign-in/*"
            element={
              <div className="flex min-h-screen items-center justify-center bg-background">
                <SignIn routing="path" path="/sign-in" />
              </div>
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <div className="flex min-h-screen items-center justify-center bg-background">
                <SignUp routing="path" path="/sign-up" />
              </div>
            }
          />

          {/* Public recommender portal — no auth required */}
          <Route
            path="/public/recommendation/:token"
            element={<RecommenderPortalPage />}
          />

          {/* Authenticated app shell */}
          <Route
            path="/*"
            element={
              <AuthGuard>
                <AppLayout />
              </AuthGuard>
            }
          >
            <Route path="applications" element={<ApplicationsPage />} />
            <Route path="programs" element={<ProgramsPage />} />
            <Route path="recommenders" element={<RecommendersPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="post-admit" element={<PostAdmitPage />} />
            <Route path="counselor" element={<CounselorPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="billing" element={<BillingPage />} />
            {/* Fallback for unknown authenticated routes — use explicit path to avoid
                matching "/" which conflicts with the top-level LandingPage route */}
            <Route path="*" element={<Navigate to="/applications" replace />} />
          </Route>
        </Routes>
      </Router>
    </ClerkProvider>
  )
}
